import { clsx } from "clsx";
import { type FC, Fragment, type PropsWithChildren, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMatch, useNavigate } from "react-router";
import { type SingleSelectListProps } from "react-stately";
import { TabItem, TabListBar, TabPanel, TriggerButton, useTabList } from "widgets";

import { type Role } from "../../graphql.js";
import { loadRole } from "../../redux/modules/roles-admin.js";
import { type IAppState } from "../../redux/reducer.js";
import { type ReduxDispatch } from "../../redux/redux-utils.js";
import PageHeader from "../core/navigation-panel/PageHeader.js";
import { ModalActionWidget } from "../core/overlays/index.js";
import styleLayout from "../core/styles/PageLayout.module.scss";
import EditRoleDialog from "./EditRoleDialog.js";
import { RoleCapabilitiesAdminCard, RolePlaybooksAdminCard, RoleUsersAdminCard } from "./RoleAdminCards.js";
import userRoleStyle from "./styles/UserRolesAdmin.module.scss";

interface IRoleAdminPageProps {
  isLoading: boolean;
}

const ADMIN_TABS = {
  users: { key: "role-users", title: "Users" },
  playbooks: { key: "role-playbooks", title: "Playbooks" },
  capabilities: { key: "role-capabilities", title: "Capabilities" },
};

const RoleAdminPage: FC<IRoleAdminPageProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const navigate = useNavigate();

  const [isLoadingRole, setLoadingRole] = useState<boolean>(false);
  const match = useMatch("/admin/roles/:selectedRoleId");
  const roles = useSelector<IAppState, Role[]>((state) => state.rolesAdmin.roles);
  const selectedRole = roles.find((role) => role.id === Number(match?.params.selectedRoleId));
  const isLoading = !selectedRole || props.isLoading || isLoadingRole;
  const [assignmentTabKey, setAssignmentTabKey] = useState<string>();

  useEffect(() => {
    if (selectedRole) {
      dispatch(loadRole(selectedRole));
    }
  }, [dispatch, selectedRole]);

  return (
    <>
      <PageHeader
        title={selectedRole?.name}
        subtitle={selectedRole?.description}
        isLoading={props.isLoading}
        actionWidgets={[
          <ModalActionWidget
            key={"edit-role-button"}
            actionWidget={({ open }) => (
              <TriggerButton
                tooltip={`Edit Role '${selectedRole?.name}'`}
                onPress={() => open()}
                isDisabled={selectedRole?.legacy ? "Roles for the Classic Portal cannot be edited" : null}
                icon={"settings_account_box"}
                label={"Edit Role"}
                showLabel
              />
            )}
          >
            {({ close }) => (
              <EditRoleDialog
                role={selectedRole}
                titleMessage={"Edit Role"}
                buttonLabel={"Save"}
                openTabKey={assignmentTabKey}
                onSelect={(role) => {
                  setLoadingRole(true);
                  navigate(`/admin/roles/${role.id}`);
                  dispatch(loadRole(role));
                  setLoadingRole(false);
                  close();
                }}
                onCancel={() => close()}
              />
            )}
          </ModalActionWidget>,
        ]}
      />
      <RoleAdminTabbedCards {...props} onSelectionChange={(key) => setAssignmentTabKey(String(key))}>
        <TabItem key={ADMIN_TABS.users.key} title={ADMIN_TABS.users.title} icon={"group"}>
          <RoleUsersAdminCard role={selectedRole} isLoading={isLoading} />
        </TabItem>
        <TabItem key={ADMIN_TABS.playbooks.key} title={ADMIN_TABS.playbooks.title} icon={"desktop_windows"}>
          <RolePlaybooksAdminCard role={selectedRole} isLoading={isLoading} />
        </TabItem>
        <TabItem key={ADMIN_TABS.capabilities.key} title={ADMIN_TABS.capabilities.title} icon={"assignment_ind"}>
          <RoleCapabilitiesAdminCard role={selectedRole} isLoading={isLoading} />
        </TabItem>
      </RoleAdminTabbedCards>
    </>
  );
};

const RoleAdminTabbedCards: FC<PropsWithChildren<SingleSelectListProps<any>>> = (props) => {
  const { ref: tabRef, state, tabListProps } = useTabList<HTMLDivElement>(props);

  return (
    <>
      <TabListBar state={state} tabListProps={tabListProps} ref={tabRef} className={userRoleStyle.tab_bar} />
      <TabPanel
        state={state}
        keepAlive
        className={clsx(styleLayout.page, userRoleStyle.fill_height)}
        childrenClassName={clsx(styleLayout.page_container, userRoleStyle.fill_height)}
      >
        {(children) => children.map((child) => <Fragment key={child.key}>{child.children}</Fragment>)}
      </TabPanel>
    </>
  );
};
export { RoleAdminPage };
